import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { OptionType, GroupType } from '@atlaskit/select';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import LazySelect from '@atlassian/jira-business-fields/src/common/ui/lazy-select/index.tsx';
import { getBoardId } from '@atlassian/jira-common-navigation/src/get-scope-data/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { transformSuggestions } from '@atlassian/jira-issue-field-sprint/src/common/utils.tsx';
import { useSprintSuggestions } from '@atlassian/jira-issue-field-sprint/src/services/suggestions/main.tsx';
import type { SprintValue } from '@atlassian/jira-issue-shared-types/src/common/types/sprint-type.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { WrapperRef } from '../../../../common/types/cell-navigation.tsx';
import LazySelectFetchError from '../../../../common/ui/lazy-select-fetch-error/index.tsx';
import { CellContentWrapper } from '../../../../common/ui/list-cell/styled.tsx';
import SelectEditError from '../../../../common/ui/select-edit-error/index.tsx';
import messages from './messages.tsx';
import { getSprintSuggestions as getSprintSuggestionsUtils, mapOptionToSprint } from './utils.tsx';

const AUTO_COMPLETE_URL = '/rest/greenhopper/1.0/sprint/picker?query=';
const SPRINT_HELP_ARTICLE = {
	href: 'https://support.atlassian.com/jira-software-cloud/docs/sprints-in-next-gen-projects/',
	helpArticleId: '4czk5C61YP1n5wpBCfITEN',
};

type Props = {
	wrapperRef?: WrapperRef;
	width: number;
	currentSprint?: SprintValue;
	onCloseMenu: () => void;
	saveValue: (value: SprintValue[], meta: null, analyticsEvent: UIAnalyticsEvent) => Promise<void>;
	issueTypeLevel?: number;
	inputValue?: string;
	onInputChange?: (value: string) => void;
	label: string;
};

export const SprintPopupContent = memo<Props>(
	({
		wrapperRef,
		width,
		onCloseMenu,
		currentSprint,
		saveValue,
		issueTypeLevel,
		inputValue,
		onInputChange,
		label,
	}: Props) => {
		const { formatMessage } = useIntl();
		const projectData = useProject();
		const projectKey = projectData.key;
		const boardId = getBoardId(typeof window !== 'undefined' ? window.location.href : '');
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const [options, setOptions] = useState<GroupType<OptionType>[]>([]);
		const [fetchError, setFetchError] = useState();

		const [{ isLoadingSuggestions }, { getSprintSuggestions, setIsLoadingSuggestions }] =
			useSprintSuggestions({ getSuggestions: getSprintSuggestionsUtils });

		const fetchSuggestions = useCallback(() => {
			setIsLoadingSuggestions(true);
			getSprintSuggestions(AUTO_COMPLETE_URL, '', projectKey)
				.then((suggestionsFromServer) => {
					setIsLoadingSuggestions(false);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const sprintOptions = transformSuggestions(
						{
							...suggestionsFromServer,
							suggestions: suggestionsFromServer?.suggestions?.filter(
								(option) => option?.id !== Number(currentSprint?.id),
							),
							allMatches: suggestionsFromServer?.allMatches?.filter(
								(option) => option?.id !== Number(currentSprint?.id),
							),
						},
						formatMessage,
					) as GroupType<OptionType>[];
					setOptions(sprintOptions);
				})
				.catch((error) => {
					setIsLoadingSuggestions(false);
					setFetchError(error);
					fireErrorAnalytics({
						meta: {
							id: 'fetchSprintSuggestions',
							packageName: 'jiraBusinessList',
							teamName: 'keystone',
						},
						attributes: { message: 'Error trying to fetch sprint suggestions' },
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				});
		}, [
			currentSprint?.id,
			formatMessage,
			getSprintSuggestions,
			projectKey,
			setIsLoadingSuggestions,
		]);

		const isSubTask = issueTypeLevel === -1;
		useEffect(() => {
			if (!isSubTask) {
				fetchSuggestions();
			}
		}, [fetchSuggestions, isSubTask]);

		const handleChange = useCallback(
			(option: OptionType | null) => {
				saveValue(
					option != null ? [mapOptionToSprint(option, boardId)] : [],
					null,
					createAnalyticsEvent({}),
				);
				if (option != null) {
					onCloseMenu();
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
				}
			},
			[boardId, createAnalyticsEvent, onCloseMenu, saveValue, wrapperRef],
		);

		const selectComponents = useMemo(() => {
			if (isSubTask) {
				return {
					NoOptionsMessage: () => (
						<SelectEditError
							message={formatMessage(messages.cannotEditSubtasks)}
							analyticsCellName="sprint"
							helpArticle={SPRINT_HELP_ARTICLE}
							onHandleLearnMore={onCloseMenu}
						/>
					),
				};
			}
			if (fetchError) {
				return {
					NoOptionsMessage: () => (
						<LazySelectFetchError
							onTryAgain={fetchSuggestions}
							message={formatMessage(messages.cannotLoadSprintSuggestions)}
							analyticsCellName="sprint"
						/>
					),
				};
			}
			return {};
		}, [fetchError, fetchSuggestions, formatMessage, isSubTask, onCloseMenu]);

		return (
			<CellContentWrapper
				data-testid="business-list.ui.list-view.sprint-cell.popup-content.cell-content-wrapper"
				preferredWidth={width}
			>
				<LazySelect
					autoFocus
					menuIsOpen
					isClearable
					placeholder={formatMessage(messages.placeholder)}
					options={options}
					onChange={handleChange}
					isInvalid={false}
					value={
						currentSprint
							? [
									{
										label: currentSprint.name,
										value: currentSprint.id.toString(),
									},
								]
							: []
					}
					isLoading={isLoadingSuggestions}
					isMulti={false}
					components={selectComponents}
					tabSelectsValue
					inputValue={inputValue}
					onInputChange={onInputChange}
					aria-label={label}
				/>
			</CellContentWrapper>
		);
	},
);
